<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="containerRef" :class="containerStyles" :key="componentKey">
    <div :class="textWrapperStyles">
      <p v-if="subtitle" :class="subheaderStyles">
        {{ subtitle }}
      </p>
      <header2 :text="title" :isUmbraco="isUmbraco" :theme="themeType" classes="mb-5" />
      <rich-text :text="text" :isUmbraco="isUmbraco" :theme="themeType" classes="mb-8" />
      <link-button
        v-if="ctaLabel && ctaLink"
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        :is-umbraco="isUmbraco"
        classes="mb-8 md:mb-0 self-start"
        :theme="themeType"
      />
    </div>
    <div :class="imgWrapperStyles" v-if="video">
      <vimeo-player
        v-if="allowVideo"
        :videoId="video"
        :portrait="portrait"
        :loop="isLooped"
      />
      <div v-else>
        <div
          class="aspect-video bg-orange flex justify-center items-center text-white text-xxl p-5"
        >
          <button @click="showCookieConsentDialog" class="btn btn-primary mt-4">
            {{ setCookie }}
          </button>
        </div>
      </div>
    </div>
    <div v-else-if="image" :class="imgWrapperStyles" ref="imageRef">
      <Transition
        enter-active-class="transition-transform duration-700 delay-500"
        leave-active-class="transition-transform duration-300"
        enter-from-class="translate-x-1/4"
        leave-to-class="translate-x-1/4"
      >
        <div v-if="stroke" :class="strokeStyles"></div>
      </Transition>
      <image-component
        :image="image"
        :alt="imgAlt"
        :portrait="portrait"
        loading="lazy"
        classes="w-full h-full object-cover"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
interface Window {
  Cookiebot?: {
    renew: () => void;
    onaccept: () => void;
    ondecline: () => void;
  };
}
import Header2 from "@components/Typography/Header2.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import ImageComponent from "@atoms/Image/Img.vue";
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import VimeoPlayer from "@molecules/VimeoPlayer/VimeoPlayer.ce.vue";
import { changeTheme, getThemeType, getCookie } from "@/util/helper";
import {
  computed,
  toRefs,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  watch,
  onBeforeMount,
} from "vue";
import { IContentBannerProps } from "@/types/components/IContentBannerProps";
import { onIntersect } from "@/composables/onIntersect";
import { getTranslations } from "@/util/api";

const props = withDefaults(defineProps<IContentBannerProps>(), {
  imgPos: "right",
  portrait: false,
  isUmbraco: false,
  imgAlt: "",
});

const {
  theme,
  title,
  subtitle,
  text,
  ctaLabel,
  ctaLink,
  target,
  imgPos,
  portrait,
  stroke,
  image,
  videoId,
  isUmbraco,
  imgAlt,
  videoLoop,
} = toRefs(props);

const containerRef: Ref<HTMLDivElement | null> = ref(null);
const imageRef: Ref<HTMLDivElement | null> = ref(null);
const observer: Ref<IntersectionObserver | null> = ref(null);
const animateStroke = ref(false);
const setCookie = ref<string | null>();
const componentKey = ref(0);

const onEnter = () => {
  changeTheme(theme.value);
  animateStroke.value = true;
};
const onExit = () => {};

const fetchInitialData = async () => {
  [setCookie.value] = await Promise.all([getTranslations("VideoCookie")]);
};

onBeforeMount(fetchInitialData);

onMounted(() => {
  updateAllowVideo();
  observer.value = onIntersect(containerRef.value, onEnter, onExit, false, {
    threshold: 0.6,
  });

  window.addEventListener("CookiebotOnAccept", onCookieConsentChange);
  window.addEventListener("CookiebotOnDecline", onCookieConsentChange);
});

onUnmounted(() => {
  // if (!observer.value) return;
  // observer.value.disconnect();
  if (observer.value) observer.value.disconnect();
  window.removeEventListener("CookiebotOnAccept", onCookieConsentChange);
  window.removeEventListener("CookiebotOnDecline", onCookieConsentChange);
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const onCookieConsentChange = () => {
  updateAllowVideo();
  componentKey.value++; // forces the entire section to re-render
};

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : "";
  return `${bgColor} sectionContainer`;
});

const imageColStart = computed(() => {
  const colStart =
    imgPos.value === "right" && portrait.value ? `lg:col-start-9` : `lg:col-start-8`;
  return colStart;
});

const imgWrapperStyles = computed(() => {
  const colStart = imgPos.value === "right" ? imageColStart.value : `lg:col-start-2`;
  const colWidth = portrait.value ? `lg:col-span-5` : `lg:col-span-6`;
  return `relative flex items-center col-span-full ${colWidth} ${colStart} lg:row-start-1 overflow-hidden`;
});

const textWrapperStyles = computed(() => {
  const colStart = imgPos.value === "right" ? `lg:col-start-2` : `lg:col-start-9`;
  return `flex flex-col justify-center col-start-2 col-span-12 mt-[80px] mb-[60px]
  lg:my-0 lg:col-span-4 ${colStart} lg:row-start-1`;
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const subheaderStyles = computed(() => {
  const color =
    isUmbraco.value && themeType.value === "dark"
      ? "text-white"
      : isUmbraco.value && themeType.value === "light"
      ? "text-aubergine"
      : "text-typography";
  return `text-xs md:text-sm font-bold mb-10 lg:mb-20 ${color} themeTransition`;
});

const video = computed(() => {
  if (!videoId) return;
  return Number(videoId?.value);
});

const strokeStyles = computed(() => {
  if (!stroke?.value) return "";
  const strokePos =
    imgPos?.value === "left"
      ? "translate-x-[calc(100%-40px)]"
      : "-translate-x-[calc(100%-40px)]";

  const animation = animateStroke.value ? strokePos : "translate-x-0";
  return `bg-${stroke?.value} absolute -inset-0 ${animation} z-10 ease-out-sine duration-700 delay-100`;
});

// const allowVideo = computed(() => {
//   const cookieString = getCookie("CookieConsent");
//   if (!cookieString) return null;
//   const marketingMatch = cookieString.match(/marketing:\s*(true|false)/);
//   console.log("marketingMatch: ", marketingMatch);

//   if (marketingMatch) {
//     // Extract the matched value and parse it to a boolean
//     const marketingValue = JSON.parse(marketingMatch[1]);
//     console.log("marketingValue: ", marketingValue);

//     return marketingValue;
//   } else {
//     console.error("Marketing property not found in cookie string");
//     return null;
//   }
// });

const allowVideo = ref<boolean | null>(null);

const updateAllowVideo = () => {
  const cookieString = getCookie("CookieConsent");
  if (!cookieString) {
    allowVideo.value = null;
    return;
  }
  const marketingMatch = cookieString.match(/marketing:\s*(true|false)/);
  if (marketingMatch) {
    allowVideo.value = JSON.parse(marketingMatch[1]);
  } else {
    console.error("Marketing property not found in cookie string");
    allowVideo.value = null;
  }
};

const isLooped = computed(() => {
  let setLoop = videoLoop?.value == true ? true : false;
  return setLoop;
});

const showCookieConsentDialog = () => {
  if (window.Cookiebot) {
    window.Cookiebot.renew();
  } else {
    console.error("Cookiebot is not available");
  }
};
</script>
